import React, { useCallback, useEffect, useState } from "react";
import styles from "../../styles/NuveeInfoHint.module.scss";

import { faTimes as dismissCross, faInfoCircle as infoIcon } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Tooltip from "@mui/material/Tooltip";
import Modal from "@mui/material/Modal";
import axios from "axios";

const storageKey = "auth_nuvee_info_hint";
const apIUrl = (path = "") => `${process.env.REACT_APP_NUVEE_BASE_URL ?? ""}${path}`;

const GetPopupContent = async (title) => {
  const res = await axios.get(apIUrl(`/feedback/${title}`));
  return res.data?.type === "popup" ? res.data : null;
};

const getImageUrl = (url) => {
  if (url?.startsWith("http") || url?.startsWith("/Images")) return url;
  const API_URL = process.env?.REACT_APP_NUVEE_BASE_URL?.split("/api")[0] || "http://localhost:1338";
  return API_URL + url;
};

const generateReactKey = (id, suffix = undefined) => {
  if (!suffix) suffix = new Date().getTime();
  suffix = suffix.toString();
  return `${id}_${suffix
    .concat(suffix)
    .split("")
    .map((char) => char.charCodeAt(0).toString(16))
    .join("")}`;
};

/**
 * Represents a hint component that displays a tooltip or a modal with additional information.
 * 
 * Adapted from nuvee_front.
 * 
 * @see [source](nuvee_front/patient/components/UI/NuveeInfoHint.tsx)
 *
 * @example
 * ```tsx
 * import NuveeInfoHint, { InfoHintType, infoTitles } from "@components/UI/NuveeInfoHint";
 * <NuveeInfoHint infoTitle={infoTitles.pro_patientViewJourney} type={InfoHintType.MODAL} accentColor="#F29086" />
 * <NuveeInfoHint text={{ content: "This is a hint", dismissContent: "Close" }} type={InfoHintType.TOOLTIP} sx={{ fontSize: "16px" }} />
 * ```
 *
 * @param {FeedBackItems} infoTitle - The name of the popup to display from Strapi Feedback type.
 * @param {string} text.content - The text to display in the hint. Priority over infoTitle if both are provided. If empty and type `tooltip`, the hint will not be displayed.
 * @param {string} text.dismissContent - The text to display on the dismiss button. If not provided or empty, no button will be displayed.
 * @param {InfoHintType} type - The type of hint to display. Defaults to modal. Modal is a popup, tooltip is a hoverable icon.
 * @param {string} accentColor - The color of the hint icon and some accents of the content. If not provided, the color `#145A79` will be used. If the infoTitle object is available, the color of the content will be the accentColor, while the InfoHint icon will be the accentColor or the default color.
 * @param {string} iconSize - The size of the icon. Default size is 1.5rem.
 * @param {React.CSSProperties} sx - The style object to apply to the hint.
 * @param {boolean} disablePortal - If true, the modal will be rendered as a child. Using portals it can help with positioning issues. Default is portal disabled.
 * @returns {React.Element | null} - The rendered NuveeInfoHint component.
 */
export default function NuveeInfoHint({ infoTitle, text, type = "modal", accentColor, iconSize, sx, disablePortal = true, customTrigger }) {
  const [infoContent, setInfoContent] = useState();
  const [openContent, setOpenContent] = useState(false);

  // Default colors.
  // For the InfoHint icon, we want to have in priority the color passed as prop or a default color.
  // For the popup accent colors, we want in priotity the color of the content
  const accentColorIcon = accentColor ?? "#145A79";
  const accentColorContent = infoContent?.accentColor ?? accentColor ?? "#145A79";

  /** Fetch the popup from Strapi.
   *
   * Store in local storage, as Record<FeedBackItems, FeedBack>.
   * - undefined entry: not fetched yet.
   * - null entry: fetched but no content.
   * - FeedBack entry: fetched and content available.
   */
  useEffect(() => {
    let isMounted = true;
    if (infoTitle && infoContent === undefined) {
      const hint = JSON.parse(localStorage.getItem(storageKey) || "{}")[infoTitle];
      if (hint === undefined) {
        GetPopupContent(infoTitle).then((res) => {
          if (isMounted) {
            setInfoContent(res);
            localStorage.setItem(
              storageKey,
              JSON.stringify({
                ...JSON.parse(localStorage.getItem(storageKey) || "{}"),
                [infoTitle]: JSON.stringify(res),
              })
            );
          }
        });
      } else {
        setInfoContent(hint && hint !== "null" ? JSON.parse(hint) : null);
      }
    }
    return () => {
      isMounted = false;
    };
  }, []);

  const openModal = useCallback(() => {
    setOpenContent(true);
  }, [infoContent]);

  function closeModal() {
    setOpenContent(false);
  }

  /**
   * Render the content of the modal based on the content props provided.
   *
   * Priorize the text content over the infoContent. Return an empty fragment if no content to display.
   */
  const renderModalContent = () => {
    if (text) return <ModalTextContent text={text.content} dismissText={text.dismissContent} closeModal={closeModal} styles={styles} />;
    if (infoContent && infoContent.type === "popup") return <ModalPopupContent popup={infoContent} closeModal={closeModal} styles={styles} />;
    return <></>;
  };

  // If we have a popup id and we have fetched its content, or if we don't have a popup id but we have a text content, we render the hint.
  const renderNuveeInfoHint = Boolean((infoTitle && infoContent) || (!infoTitle && text?.content));

  /**
   * Map the type of hint to the JSX element to render.
   */
  const renderTypeMap = {
    ["modal"]: (
      <>
        {customTrigger?.({ openModal, accentColor }) ?? <FontAwesomeIcon icon={infoIcon} onClick={openModal} color={accentColorIcon} />}

        <Modal open={openContent} aria-describedby="modal-modal-description" onClose={closeModal} disablePortal={disablePortal}>
          <div
            id={"nihModalWrapper"}
            className={styles.nihModalWrapper}
            style={{ "--accent-color": accentColorContent, ...(iconSize && { "--nuvee-infohint": iconSize }) }}
          >
            <div className={styles.dismissCross} onClick={closeModal}>
              <FontAwesomeIcon icon={dismissCross} color={accentColorContent} />
            </div>
            {renderModalContent()}
          </div>
        </Modal>
      </>
    ),
    ["tooltip"]: (
      <Tooltip
        title={text?.content ?? ""} // Text content should not be empty because we call this map only if we have a text content.
        placement="left"
        enterTouchDelay={0}
        leaveTouchDelay={5000}
        open={openContent}
        onClose={closeModal}
        onOpen={openModal}
        componentsProps={{
          tooltip: {
            sx: {
              fontSize: "16px",
            },
          },
        }}
      >
        <span>
          <FontAwesomeIcon icon={infoIcon} color={accentColorIcon} style={iconSize ? { width: iconSize, height: iconSize } : {}} />
        </span>
      </Tooltip>
    ),
  };

  return renderNuveeInfoHint ? (
    <div
      className={`${styles.NuveeInfoHint} ${styles[type]}`}
      style={{
        "--accent-color": accentColorContent,
        ...(iconSize && { "--nuvee-infohint": iconSize }),
        ...sx,
      }}
    >
      {renderTypeMap[type]}
    </div>
  ) : null;
}

/**
 * Render a modal content as a simple text with a dismiss button.
 *
 * @param {string} text - The text to display in the modal.
 * @param {string} dismissText - The text to display on the dismiss button. If not provided, no button will be displayed.
 * @param {() => void} closeModal - The function to call when the modal should be closed.
 * @param {typeof styles} styles - The css module to use for the styles.
 */
const ModalTextContent = ({ text, dismissText, closeModal, styles }) => {
  const textLines = text.split("\n");
  return (
    <div className={`${styles.contentContainer} ${styles.text}`}>
      <div id="modal-modal-description">
        {textLines.map((line, index) => (
          <React.Fragment key={generateReactKey("line", index)}>{line ? <p>{line}</p> : <br />}</React.Fragment>
        ))}
      </div>
      {dismissText && <button onClick={closeModal}>{dismissText}</button>}
    </div>
  );
};

/**
 * Render a modal content as a `FeedBack` popup.
 *
 * @param {FeedBack} popup - The content of the popup to display.
 * @param {() => void} closeModal - The function to call when the modal should be closed.
 * @param {typeof styles} styles - The css module to use for the styles.
 */
const ModalPopupContent = ({ popup, closeModal, styles }) => {
  const content = popup.content[0];
  return (
    <div className={`${styles.contentContainer} ${styles.popup}`}>
      <div className={styles.title}>
        {content.icon?.url && <img src={getImageUrl(content.icon.url)} alt={content.icon.alternativeText} />}
        <h2>{content.title}</h2>
      </div>
      {content.text && (
        <div id="modal-modal-description" className={styles.text}>
          {content.text?.split("\n").map((line, index) => (
            <p key={generateReactKey("line", index)}>{line}</p>
          ))}
        </div>
      )}
      {(content.dismissButton || content.cta) && (
        <div className={styles.actionsContainer}>
          {content.cta && (
            <button className={styles.cta} onClick={() => window.open(content.cta?.link, "_blank")}>
              {content.cta.title}
            </button>
          )}
          {content.dismissButton && (
            <button className={styles.dismiss} onClick={closeModal}>
              {content.dismissButton}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
